import {Component, ElementRef, Injector, Input, OnInit, ViewChild} from '@angular/core';
import { Camera, CameraOptions } from '@ionic-native/camera/ngx';
import {NavParams, Platform} from '@ionic/angular';
import { forkJoin, Observable, of } from 'rxjs';
import { Extender } from 'src/shared/helpers/extender';
import { Account, Feed } from 'src/shared/models';
import { v4 as uuid } from 'uuid';
import { FeedService } from '../../services/feed/feed.service';

@Component({
  selector: 'app-add',
  templateUrl: './add.component.html',
  styleUrls: ['./add.component.scss']
})
export class AddComponent extends Extender implements OnInit {

  /**
   * @property fileUpload
   * @type ElementRef
   */
  @ViewChild('fileUpload', {static: false}) public fileUpload: ElementRef;

  /**
   * @property type
   * @type string
   */
  @Input() public type: string;

  /**
   * @property me
   * @type Contact
   */
  public me: Account;

  /**
   * @property feed
   * @type Feed
   */
  public feed: Feed = null;

  /**
   * @property id
   * @type string
   */
  public id: string = null;

  /**
   * @property feedOptions
   * @type any
   */
  public feedOptions: any;
  public more: boolean = false;
  public imageFilesToUploads: any[] = [];
  public cameraOptions: CameraOptions = {
    quality: 100,
    destinationType: this.camera.DestinationType.DATA_URL,
    encodingType: this.camera.EncodingType.JPEG,
    mediaType: this.camera.MediaType.PICTURE
  };
  public feedImages: string[] = [];

  constructor(
    protected injector: Injector,
    private navParams: NavParams,
    private camera: Camera,
    private feedService: FeedService,
    private platform: Platform
  ) {
    super(injector);
  }

  public async ngOnInit() {
    if (this.navParams.get('type')) {
      this.type = this.navParams.get('type');
    }
    this.me = await this.auth$.getUser();
    this.feed = this.navParams.get('data');
    if (!this.feed) {
      this.feed = {
        contact: this.me,
        content: '',
        images: [],
        subtitle: '',
        title: '',
        createdAt: Date.now(),
        uid: this.me.uid
      };
    }
    this.configfeed();
    if (this.feed && this.feed.id) { this.id = this.feed.id; }
  }

  /**
   * configure buttons for items to add to feed
   * @method configfeed
   */
  public configfeed() {
    this.feedOptions = [
      {
        icon: 'camera',
        click: () => {
          this.openImageOptions();
        }
      },
      {
        icon: 'spell-check',
        click: () => {
          this.more = !this.more;
        }
      }
    ];
  }

  public async openImageOptions(): Promise<any> {
    if (this.platform.is('cordova')) {
      const actionSheetCtrl = await this.actionSheetCtrl.create({
        header: this.translate.instant('FeedModule.Add.image-option-header'),
        buttons: [
          {
            text: this.translate.instant('FeedModule.Add.image-option-library'),
            handler: () => {
              this.cameraOptions.sourceType = 0;
              this.takePicture(this.cameraOptions);
            }
          },
          {
            text: this.translate.instant('FeedModule.Add.image-option-camera'),
            handler: () => {
              this.cameraOptions.sourceType = 1;
              this.takePicture(this.cameraOptions);
            }
          },
          {
            text: this.translate.instant('FeedModule.Add.image-option-multi'),
            handler: () => {
              this.getPicturesFromLibrary(5);
            }
          },
          {
            text: this.translate.instant('Other.close'),
            role: 'cancel'
          }
        ]
      });
      await actionSheetCtrl.present();
    } else { this.getPictureFromWeb(); }
  }

  /**
   * remove uploaded images
   * @method removeImage
   * @param index
   */
  public removeImage(index): void {
    this.feedImages.splice(index, 1);
  }

  public uploadImagesToFirebase(): Observable<any> {
    if (this.imageFilesToUploads.length > 0) {
      return forkJoin(this.imageFilesToUploads);
    }
    return of(null);
  }

  /**
   * @description dismiss modal with feed in dismiss parameter used by wall component to added to the list of feed
   * @method save
   * @public
   * @returns void
   */
  public save(): void {
    this.uploadImagesToFirebase().subscribe((results) => {
      this.feed.images = results;
      if (this.id) { this.feed.id = this.id; }
      if (!this.feed.id) {
        this.feed.createdAt = Date.now();
        this.feed.uid = this.me.uid;
      }
      if (this.type) { this.feed.type = this.type; }
      delete this.feed.contact;
      this.loading = true;
      this.feedService.addUpdateFeed(this.feed).then(
        (_data) => {
          this.loading = false;
          this.closeModal();
        },
        (error) => this.toast(error)
      );
    });
  }

  /**
   * @description handle file submission from browser image selection
   * @method uploadFile
   * @param event
   */
  public async uploadFile(event) {
    if (event && event.target && event.target.files && event.target.files[0] && event.target.files[0].type.match(/^image\//)) {
      const file = await this.convertFileToBase64(event.target.files[0]);
      this.imageFilesToUploads.push(
          this.uploadImage(file, `${uuid()}.jpg`, 'blog-images')
      );
    } else {
      this.toast('No file or incorrect file type selected');
    }
  }

  /**
   * @description take picture from your devices camera using the camera plugin,
   * add result to images property to display how many on screen and in preview
   * store upload functions for each image in an array 'imageFilesToUploads' to use at save
   * @method takePicture
   * @param max
   */
  private takePicture(cameraOptions: any) {
    this.camera.getPicture(cameraOptions).then(
      (imageData) => {
        imageData = 'data:image/jpeg;base64,' + imageData;
        this.feedImages.push(imageData);
        this.imageFilesToUploads.push(
          this.uploadImage(imageData, `${uuid()}.jpg`, 'blog-images', true)
        );
      },
      (error) => {
        this.status = 'error';
        this.toast(error);
      }
    );
  }

  /**
   * @description get a picture from the browser when cordova is not available
   * @method getPictureFromWeb
   */
  private async getPictureFromWeb() {
    this.fileUpload.nativeElement.click();
  }

  /**
   * @description convert file received from browser base64
   * @method convertFileToBase64
   * @param file
   */
  private async convertFileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader  = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        resolve(reader.result);
      };
    });
  }

  /**
   * @description get pictures from your devices library using the image picker plugin,
   * add results to images property to display how many on screen and in preview
   * store upload functions for each image in an array 'imageFilesToUploads' to use at save
   * @method getPicturesFromLibrary
   * @param max
   */
  private getPicturesFromLibrary(max): void {
    this.getPictures(max).then((results) => {
      if (results && results.length > 0) {
        this.feedImages = [...results];
        results
          .forEach((imageUrl: any) => {
            this.imageFilesToUploads.push(
              this.uploadImage(imageUrl, `${uuid()}.jpg`, 'blog-images')
            );
          })
          .catch((error: any) => {
            this.toast(error);
          });
      }
    });
  }
}
